import { medusaClient } from "./config";

const PRODUCT_EXPAND = "tags,variants,variants.prices,collection,categories";

export const fetchAllProducts = async (sort, limit = 12, offset = 0, collectionIds = [], categoryIds = []) => {
  // Use large page and paginate on client to ensure stable paging across backends
  const params = {
    limit: 99999,
    offset: 0,
  };
  if (sort === "latest") params["order"] = "-created_at";
  else if (sort?.includes("price")) params["description"] = sort;
  // else if (sort?.includes("popular")) {
  //   params["expand"] = "tags,variants,variants.prices,collection,categories";
  // }
  params["expand"] = PRODUCT_EXPAND;
  if (collectionIds?.length) params["collection_id"] = collectionIds;
  if (categoryIds?.length) params["category_id"] = categoryIds;
  const { products, count } = await medusaClient.products.list(params);
  if (!products.length) {
    throw new Error("Network response was not ok");
  }

  if (sort?.includes("popular")) {
    try {
      const sortedProducts = sortByFeaturedTag(products);
      const sorted = sortedProducts.slice(offset, offset + limit);
      return [sorted,count];
    } catch (err) {
      return [products.slice(offset, offset + limit),count];
    }

    // return sortedProducts.slice(offset, offset + limit);
  } else return [products.slice(offset, offset + limit),count];
};

export const fetchLatestProducts = async (limit = 4, offset = 0) => {
  const { products, count } = await medusaClient.products.list({
    limit,
    offset,
    order: "-created_at",
    expand: PRODUCT_EXPAND,
  });

  return [products || [], count || 0];
};

export const fetchProductsByTagValue = async (tagValue, limit = 8, offset = 0, sort = "latest") => {
  const { product_tags } = await medusaClient.productTags.list({
    value: tagValue,
    limit: 1,
  });
  const tagId = product_tags?.[0]?.id;

  if (!tagId) return [[], 0];

  const params = {
    limit,
    offset,
    tags: [tagId],
    expand: PRODUCT_EXPAND,
  };

  if (sort === "latest") params["order"] = "-created_at";

  const { products, count } = await medusaClient.products.list(params);

  return [products || [], count || 0];
};

export const fetchPrioritizedLatestProducts = async (tagValue = "new-priority", limit = 8) => {
  const [latestResult, priorityResult] = await Promise.allSettled([
    fetchLatestProducts(limit, 0),
    fetchProductsByTagValue(tagValue, limit, 0),
  ]);

  if (latestResult.status === "rejected" && priorityResult.status === "rejected") {
    throw latestResult.reason;
  }

  const [latestProducts, latestCount] =
    latestResult.status === "fulfilled" ? latestResult.value : [[], 0];
  const [priorityProducts] =
    priorityResult.status === "fulfilled" ? priorityResult.value : [[], 0];
  const seen = new Set();
  const products = [...priorityProducts, ...latestProducts].filter((product) => {
    const key = product?.id || product?.handle;

    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return [products.slice(0, limit), latestCount];
};

export const fetchProducts = async (slug, sort, limit = 12, offset = 0, categoryIds = []) => {
  const { collections } = await medusaClient.collections.list({
    handle: [slug],
  });
  const collectionId = collections?.[0]?.id;
  if (!collectionId) return [[], 0];

  // Use large page and paginate on client for consistency
  const params = {
    limit: 99999,
    offset: 0,
    collection_id: [collectionId],
  };
  if (sort === "latest") params["order"] = "-created_at";
  else if (sort?.includes("price")) params["description"] = sort;
  // else if (sort?.includes("popular")) {
  //   params["expand"] = "tags,variants,variants.prices,collection,categories";
  // }
  params["expand"] = PRODUCT_EXPAND;
  if (categoryIds?.length) params["category_id"] = categoryIds;
  const { products, count } = await medusaClient.products.list(params);
  if (!products.length) return [[], count || 0];

  if (sort?.includes("popular")) {
    try {
      const sortedProducts = sortByFeaturedTag(products);
      const sorted = sortedProducts.slice(offset, offset + limit);
      return [sorted,count];
    } catch (err) {
      return [products.slice(offset, offset + limit),count];
    }

    // return sortedProducts.slice(offset, offset + limit);
  } else return [products.slice(offset, offset + limit),count];
};

function sortByFeaturedTag(products) {
  return products.sort((a, b) => {
    const hasFeaturedA = a.tags.some((t) => t.value === "featured");
    const hasFeaturedB = b.tags.some((t) => t.value === "featured");

    // Prioritize products with the "featured" tag
    if (hasFeaturedA && !hasFeaturedB) {
      return -1;
    } else if (!hasFeaturedA && hasFeaturedB) {
      return 1;
    } else {
      // If both have or neither have the "featured" tag, maintain original order
      return 0;
    }
  });
}
export const fetchCollections = async () => {
  const limit = 100;
  let offset = 0;
  let allCollections = [];
  let totalCount = Infinity;

  while (offset < totalCount) {
    const { collections, count } = await medusaClient.collections.list({
      limit,
      offset,
    });

    if (!collections?.length) {
      break;
    }

    allCollections = allCollections.concat(collections);
    totalCount = typeof count === "number" ? count : allCollections.length;
    offset += collections.length;
  }

  if (!allCollections.length) {
    throw new Error("Network response was not ok");
  }
  return allCollections;
};

export const fetchCategories = async () => {
  try {
    const res = await medusaClient.productCategories?.list({ limit: 99 });
    const list = res?.product_categories || res?.categories || [];
    return list;
  } catch (e) {
    // Some older SDKs expose categories under a different key
    try {
      const res2 = await medusaClient.categories?.list({ limit: 99 });
      return res2?.categories || [];
    } catch (err) {
      return [];
    }
  }
};

// Returns unique categories for a given collection handle
export const fetchCategoriesForCollectionHandle = async (handle) => {
  try {
    const { collections } = await medusaClient.collections.list({ handle: [handle] });
    const collectionId = collections?.[0]?.id;
    if (!collectionId) return [];
    // Fetch all products for this collection just to compute category coverage
    const { products } = await medusaClient.products.list({
      collection_id: [collectionId],
      expand: "categories",
      limit: 9999,
      offset: 0,
    });
    const map = new Map();
    (products || []).forEach((p) => {
      (p.categories || []).forEach((c) => {
        if (!map.has(c.id)) map.set(c.id, c);
      });
    });
    return Array.from(map.values());
  } catch (e) {
    return [];
  }
};
